import { Badge } from "@/components/ui";
import { cn, SQUARE_FOOTAGE } from "@/lib/utils";
import { setMetricData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { useGetDistributionMetricByRuleId, useGetDistributionRuleMethodStatusMatrix } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const useSquareFootage = () => {
  const dispatch = useDispatch();
  const tableRef = useRef<any>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  
  const { communityData, currentFinanceYear } = useSelector(
    (state: RootState) => state.currentData
  );

  const communityName =
    communityData?.communityName || communityData?.name || "";
  
  const {data: communityRules, isLoading: isLoadingRules } = useGetDistributionRuleMethodStatusMatrix({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  })

  const ruleId = useMemo(() => communityRules?.data?.find((data: any) => data?.method == SQUARE_FOOTAGE)?.distributionRuleId, [communityRules]);

  const {data: metricData, isLoading: isLoadingMetricInfo } = useGetDistributionMetricByRuleId({
    communityId: communityData?.communityId,
    ruleId: ruleId,
    queryParams: {
      enabled: !!(ruleId)
    }
  });

  useEffect(() => {
    if (metricData) {
      dispatch(setMetricData(metricData));
    }
  }, [metricData])
  
  const tableData = useMemo(() => {
    const nodes = metricData?.data?.nodes;
    if (!nodes || !Array.isArray(nodes)) return [];
  
    // 1. Group items by their parentName for O(1) fast lookup performance
    const parentMap = new Map<string, any[]>();
    
    nodes.forEach(node => {
      // We normalize keys to lower case to eliminate casing bugs
      const parentKey = node.parentName ? node.parentName.toLowerCase() : "root";
      if (!parentMap.has(parentKey)) {
        parentMap.set(parentKey, []);
      }
      const _node = parentKey == "root" ? {...node, parentName: communityName} : {...node}
      parentMap.get(parentKey)!.push({ ..._node, children: [] });
    });
  
    // 2. Recursive function to construct multi-level children branches
    const buildTree = (parentNameKey: string): any[] => {
      const directChildren = parentMap.get(parentNameKey) || [];
      
      return directChildren.map(child => {
        // Find and nest deeper grand-children recursively
        const childNameKey = child.name.toLowerCase();
        if (parentMap.has(childNameKey)) {
          child.children = buildTree(childNameKey);
        }
        return child;
      });
    };
  
    // 3. Initiate the generation step from the absolute root level elements
    return buildTree("root");
  }, [metricData]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "name",
      header: <div>Level</div>,
      accessorFn: (row: any) => `${row?.level}-${row?.name}`
    },
    {
      accessorKey: "parentName",
      header: <div>Parent</div>
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      cell: ({row}: any) => row?.original?.metricValue ? Number(row?.original?.metricValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const isActive = row?.original?.status?.toLowerCase() == "active"
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive
                  ? "text-green-600 border-green-600 bg-green-50"
                  : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive
                ? "Active"
                : "Inactive"}
          </Badge>
        );
      },
    }
  ];

  // Define Table Columns
  const subColumns = [
    {
      accessorKey: "name",
      header: <div>Level</div>,
      accessorFn: (row: any) => `${row?.level}-${row?.name}` 
    },
    {
      accessorKey: "parentName",
      header: <div>Parent</div>
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      accessorFn: (row: any) => row?.metricValue ? Number(row?.metricValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        const isActive = row?.status?.toLowerCase() == "active"
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive
                  ? "text-green-600 border-green-600 bg-green-50"
                  : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive
                ? "Active"
                : "Inactive"}
          </Badge>
        );
      },
    }
  ];

  // Define Table Columns
  const level2Columns = [
    {
      accessorKey: "name",
      header: <div>Level</div>,
      accessorFn: (row: any) => `${row?.level}-${row?.name?.split(" - ")?.[0] || ""}`
    },
    {
      accessorKey: "parentName",
      header: <div>Parent</div>
    },
    {
      accessorKey: "type",
      header: <div>Type</div>
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      accessorFn: (row: any) => row?.metricValue ? Number(row?.metricValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        const isActive = row?.status?.toLowerCase() == "active"
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive
                  ? "text-green-600 border-green-600 bg-green-50"
                  : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive
                ? "Active"
                : "Inactive"}
          </Badge>
        );
      },
    }
  ];

  const onExpandCollapse = () => {
    const nextExpandedState = !isAllExpanded;
  
    if (nextExpandedState) {
      tableRef.current?.expandAll();
    } else {
      tableRef.current?.collapseAll();
    }
    setIsAllExpanded(nextExpandedState);
  };

  const onExpandStateChange = (_: any, expanded: boolean) => {
    setIsAllExpanded(expanded);
  }

  return {
    columns,
    subColumns,
    level2Columns,
    tableData,
    isLoadingRules,
    isLoadingMetricInfo,
    tableRef,
    isAllExpanded,
    onExpandCollapse,
    onExpandStateChange
  }
}

export default useSquareFootage;